if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.addEventListener('keydown', this.containKeyboardFocus.bind(this), true);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      containKeyboardFocus(event) {
        if (event.key !== 'Tab' || !this.hasAttribute('open')) return;
        // The shared Dawn trap caches controls and treats the outer custom element
        // as the initial focus target. Quick add focuses its inner dialog instead,
        // and variant changes can disable controls after that cached snapshot.
        event.stopPropagation();
        const controls = Array.from(this.querySelectorAll(
          'a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]'
        )).filter((control) => {
          if (control.tabIndex < 0 || control.matches(':disabled') || control.closest('[hidden], [inert]')) return false;
          for (let ancestor = control; ancestor && ancestor !== this; ancestor = ancestor.parentElement) {
            if (ancestor.tagName === 'DETAILS' && !ancestor.open && !ancestor.querySelector(':scope > summary')?.contains(control)) return false;
            const style = getComputedStyle(ancestor);
            if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
          }
          return true;
        });
        const active = document.activeElement;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!controls.includes(active) || (event.shiftKey ? active === first : active === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        }
      }

      connectedCallback() {
        if (this.moved) return;
        document.querySelectorAll('quick-add-modal').forEach((modal) => {
          if (modal !== this && modal.id === this.id && modal.moved) {
            if (modal.hasAttribute('open')) modal.hide();
            modal.remove();
          }
        });
        super.connectedCallback();
      }

      hide(preventFocus = false) {
        if (this.openedBy && !this.openedBy.isConnected) {
          this.openedBy = document.getElementById(this.openedBy.id) || document.getElementById('ProductGridContainer');
        }
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        const template = this.querySelector('template[data-quick-add-template]');
        if (template) {
          this.modalContent.replaceChildren(template.content.cloneNode(true));
          super.show(opener);
          return;
        }

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => {
            if (!response.ok) throw new Error('Unable to load product');
            return response.text();
          })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Unsupported product template');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .catch(() => {
            const message = document.createElement('p');
            message.setAttribute('role', 'alert');
            message.textContent = this.dataset.loadError || window.cartStrings?.error || '';
            const link = document.createElement('a');
            link.href = opener.getAttribute('data-product-url');
            link.textContent = this.dataset.detailsLabel || opener.textContent.trim();
            this.modalContent.replaceChildren(message, link);
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

customElements.whenDefined('product-form').then(() => {
  if (customElements.get('lnvc-quick-add-form')) return;
  customElements.define('lnvc-quick-add-form', class extends customElements.get('product-form') {
    constructor() {
      super();
      this.selector = this.querySelector('[data-variant-select]');
      this.quantity = this.querySelector('[name="quantity"]');
      this.selector.addEventListener('change', () => this.updateVariant());
      this.updateVariant();
    }

    updateVariant() {
      const variant = Array.from(this.selector.options).find((option) => option.value === this.selector.value);
      const available = variant?.dataset.available === 'true';
      this.submitButton.disabled = !available;
      this.submitButtonText.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      this.submitButton.removeAttribute('aria-disabled');
      this.handleErrorMessage();
      if (!variant) return;
      this.querySelector('[data-variant-price]').textContent = variant.dataset.price;
      this.quantity.min = variant.dataset.min || '1';
      this.quantity.step = variant.dataset.step || '1';
      if (variant.dataset.max) this.quantity.max = variant.dataset.max;
      else this.quantity.removeAttribute('max');
      this.quantity.value = this.quantity.min;
      this.querySelector('[data-quantity-rules]').textContent = variant.dataset.rules;
      const details = this.querySelector('a');
      const url = new URL(details.href);
      url.searchParams.set('variant', variant.value);
      details.href = url.toString();
    }

    handleErrorMessage(message) {
      super.handleErrorMessage(message);
      if (message) this.errorMessageWrapper?.scrollIntoView?.({block: 'nearest'});
    }

    async onSubmitHandler(event) {
      event.preventDefault();
      if (this.pending || this.submitButton.disabled || !this.form.reportValidity()) return;
      this.pending = true;
      this.handleErrorMessage();
      const formData = new FormData(this.form);
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      this.selector.disabled = true;
      this.quantity.disabled = true;
      this.querySelector('.loading__spinner').classList.remove('hidden');
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(this.closest('quick-add-modal').openedBy);
      }
      config.body = formData;
      let added = false;
      try {
        const response = await fetch(routes.cart_add_url, config);
        const result = await response.json();
        if (!response.ok || result.status) {
          this.handleErrorMessage(typeof result.description === 'string' ? result.description : this.dataset.errorLabel);
          publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: formData.get('id'), errors: result.errors || result.description, message: result.message });
          return;
        }
        added = true;
        if (!this.cart) {
          window.location.assign(window.routes.cart_url);
          return;
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: result });
        const modal = this.closest('quick-add-modal');
        if (modal?.contains(this)) modal.hide(true);
        this.cart.classList.remove('is-empty');
        this.cart.renderContents(result);
      } catch {
        if (added) window.location.assign(window.routes.cart_url);
        else this.handleErrorMessage(this.dataset.errorLabel);
      } finally {
        this.pending = false;
        if (added) this.submitButton.disabled = true;
        this.selector.disabled = false;
        this.quantity.disabled = false;
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }
    }
  });
});
